import { IntervalsRange } from "@/engine/engine";
import { calculateIntersectionOfRecordWithIntervals } from "./intersection";
import { countN, shiftN } from "./intervals";
import { getChildrenTypes, getChildType } from "./intervalTypes";
import { getName, getShortName } from "./names";
import {
  HGIntersection,
  HGIntersectionType,
  HGInterval,
  HGIntervalType,
  HGTimeRecordPlus,
} from "./types";

/**
 * Builds the record tree rendered by the Records timeline.
 *
 * Every level of the tree — the active interval at the root, the groups below
 * it, the subgroups below those — is the same {@link LevelNode} shape with the
 * same five slots, rendered in a fixed order:
 *
 *   exact -> starts -> (children | leaves) -> ends -> transit
 *
 * "children" are the deeper interval levels (plus gaps between them); "leaves"
 * take their place once the level chain runs out. That uniformity is what lets
 * a single recursive renderer handle arbitrary depth.
 */

// A record spanning more n values than this is placed at its start and end only,
// even in "every" mode: iterating the whole span would freeze the UI without
// telling the user anything they can read.
const MAX_SPAN_EXPANSION = 5000;

export type BucketKind = "exact" | "starts" | "ends" | "transit";

export const BUCKET_KINDS: BucketKind[] = ["exact", "starts", "ends", "transit"];

export interface RecordNode {
  key: string;
  record: HGTimeRecordPlus;
  intersection: HGIntersection;
  /** This record was already rendered earlier in document order. */
  repeat: boolean;
}

export interface GapNode {
  kind: "gap";
  key: string;
  /** First missing sub-interval. */
  from: HGInterval;
  /** Last missing sub-interval. */
  to: HGInterval;
  count: number;
}

export interface LevelNode {
  kind: "level";
  key: string;
  /** 0 = the active interval, 1 = group, 2 = subgroup, ... */
  depth: number;
  interval: HGInterval;
  endInterval: HGInterval | null;
  name: string;
  shortName: string;
  buckets: Record<BucketKind, RecordNode[]>;
  /** Deeper interval levels, interleaved with gaps. */
  children: (LevelNode | GapNode)[];
  /** Records with nowhere deeper to go. Occupies the same slot as `children`. */
  leaves: RecordNode[];
  /** Number of records anywhere below this node. */
  total: number;
}

export interface RecordTree {
  root: LevelNode;
  /** Resolved interval type chain below the root, e.g. ["m", "d"]. */
  levelTypes: HGIntervalType[];
  total: number;
}

export type SpanningMode = "ends" | "first" | "every";

export interface RecordTreeOptions {
  groupBy: "auto" | HGIntervalType;
  thenBy: "auto" | "none" | HGIntervalType;
  order: "asc" | "desc";
  spanning: SpanningMode;
  showGaps: boolean;
}

export function isLevelNode(node: LevelNode | GapNode): node is LevelNode {
  return node.kind === "level";
}

/** Every level key a collapse-all / expand-all has to touch. */
export function collectToggleKeys(tree: RecordTree): string[] {
  const levelKeys: string[] = [];

  const walkLevel = (level: LevelNode) => {
    if (level.depth > 0) levelKeys.push(level.key);
    for (const child of level.children) {
      if (isLevelNode(child)) walkLevel(child);
    }
  };

  walkLevel(tree.root);
  return levelKeys;
}

// --- naming -----------------------------------------------------------------

// getName() throws for interval types it has no format for, which is a normal
// runtime case (a type coming from a persisted stack or the API).
export function safeName(interval: HGInterval): string {
  try {
    return getName(interval);
  } catch {
    return `${interval.it} ${interval.n}`;
  }
}

export function safeShortName(interval: HGInterval): string {
  try {
    return getShortName(interval);
  } catch {
    return interval.n.toString();
  }
}

// --- n arithmetic -----------------------------------------------------------

// The "no n == 0" rule lives in intervals.ts; these just name the two steps.
function nextN(n: bigint): bigint {
  return shiftN(n, 1n);
}

function prevN(n: bigint): bigint {
  return shiftN(n, -1n);
}

// --- level type resolution --------------------------------------------------

/**
 * The interval types a level of type `it` may be split by. `getChildType()` can
 * name a type outside `childrenTypes` (a century's default child is "y", not
 * "dec"), so it is folded in explicitly.
 */
function splitOptionsFor(it: HGIntervalType | undefined): HGIntervalType[] {
  if (!it) return [];
  const children = getChildrenTypes(it);
  const preferred = getChildType(it);
  if (preferred && !children.includes(preferred)) return [preferred, ...children];
  return children;
}

/** Interval types offered by the toolbar's "Group by" control. */
export function groupByOptions(
  interval: HGInterval,
  endInterval: HGInterval | null,
): HGIntervalType[] {
  const options = splitOptionsFor(interval.it);
  // A range covering several units of its own type is grouped by that type:
  // two years of records are months only if you ask for months.
  const fromStart = spansMultipleUnits(interval, endInterval)
    ? [interval.it, ...options]
    : options;

  // The two endpoints can sit at different levels, and the finer one carries
  // groupings the start knows nothing about — a year-to-day range should still
  // offer days. Order stays coarse -> fine because the end is the finer side.
  if (!endInterval || endInterval.it === interval.it) return fromStart;
  const fromEnd = [endInterval.it, ...splitOptionsFor(endInterval.it)];
  return [...fromStart, ...fromEnd.filter((it) => !fromStart.includes(it))];
}

/** Interval types offered by the toolbar's "Then by" control. */
export function thenByOptions(groupBy: HGIntervalType): HGIntervalType[] {
  return splitOptionsFor(groupBy);
}

function spansMultipleUnits(
  interval: HGInterval,
  endInterval: HGInterval | null,
): boolean {
  if (!endInterval) return false;
  const [first, last] = IntervalsRange(interval, endInterval, interval.it);
  return last > first;
}

export function autoGroupBy(
  interval: HGInterval,
  endInterval: HGInterval | null,
): HGIntervalType | undefined {
  return spansMultipleUnits(interval, endInterval)
    ? interval.it
    : getChildType(interval.it);
}

/**
 * Turns the stored "Group by" / "Then by" choices into a concrete chain of
 * interval types. A stored type that makes no sense at the current position
 * (the user navigated from a year to a second) silently falls back to auto.
 */
export function resolveLevelTypes(
  interval: HGInterval,
  endInterval: HGInterval | null,
  options: Pick<RecordTreeOptions, "groupBy" | "thenBy">,
): HGIntervalType[] {
  const auto = autoGroupBy(interval, endInterval);
  const allowedGroups = groupByOptions(interval, endInterval);

  let group = options.groupBy === "auto" ? auto : options.groupBy;
  if (!group || !allowedGroups.includes(group)) group = auto;
  if (!group) return [];

  const levels = [group];
  if (options.thenBy === "none") return levels;

  const autoThen = getChildType(group);
  const allowedThen = thenByOptions(group);
  let then = options.thenBy === "auto" ? autoThen : options.thenBy;
  if (!then || !allowedThen.includes(then)) then = autoThen;
  if (then) levels.push(then);

  return levels;
}

// --- sorting ----------------------------------------------------------------

function shouldTakeBegin(intersectionType: HGIntersectionType) {
  return (
    intersectionType === HGIntersectionType.Contained ||
    intersectionType === HGIntersectionType.PartRight ||
    intersectionType === HGIntersectionType.Exact
  );
}

// A record is placed by the edge that falls inside the interval: one that runs
// out the right side sorts by where it began, one that runs in from the left by
// where it ends.
function sortSecond(node: RecordNode): bigint {
  if (shouldTakeBegin(node.intersection.intersectionType)) {
    return node.record.start_second;
  }
  return node.record.end_second ?? node.record.start_second;
}

function compareRecordNodes(a: RecordNode, b: RecordNode): number {
  const sa = sortSecond(a);
  const sb = sortSecond(b);
  if (sa !== sb) return sa < sb ? -1 : 1;

  // Same instant: whatever covers the whole interval frames the rest.
  const ea = a.intersection.intersectionType === HGIntersectionType.Exact ? 0 : 1;
  const eb = b.intersection.intersectionType === HGIntersectionType.Exact ? 0 : 1;
  if (ea !== eb) return ea - eb;

  // Ids keep the order stable across rebuilds.
  return a.record.id < b.record.id ? -1 : a.record.id > b.record.id ? 1 : 0;
}

// --- building ---------------------------------------------------------------

interface BuildContext {
  levelTypes: HGIntervalType[];
  options: RecordTreeOptions;
}

function makeRecordNode(
  record: HGTimeRecordPlus,
  intersection: HGIntersection,
  ownerKey: string,
): RecordNode {
  return {
    key: `${ownerKey}/rec:${record.id}`,
    record,
    intersection,
    repeat: false,
  };
}

function buildLevel(
  ctx: BuildContext,
  records: HGTimeRecordPlus[],
  interval: HGInterval,
  endInterval: HGInterval | null,
  depth: number,
  name: string,
): LevelNode {
  const key =
    `lvl:${interval.it}:${interval.n}` +
    (endInterval ? `:${endInterval.it}:${endInterval.n}` : "");

  const node: LevelNode = {
    kind: "level",
    key,
    depth,
    interval,
    endInterval,
    name,
    shortName: safeShortName(interval),
    buckets: { exact: [], starts: [], ends: [], transit: [] },
    children: [],
    leaves: [],
    total: 0,
  };

  const childIt: HGIntervalType | undefined = ctx.levelTypes[depth];

  // Records that render at this level, and records that pass through it on the
  // way to a sub-interval.
  const placed: {
    record: HGTimeRecordPlus;
    intersection: HGIntersection;
    exact: boolean;
  }[] = [];
  const descend: HGTimeRecordPlus[] = [];

  for (const record of records) {
    const intersection = calculateIntersectionOfRecordWithIntervals(
      record,
      interval,
      endInterval,
    );
    switch (intersection.intersectionType) {
      case HGIntersectionType.None:
        continue;
      case HGIntersectionType.Starts:
        node.buckets.starts.push(makeRecordNode(record, intersection, key));
        continue;
      case HGIntersectionType.Ends:
        node.buckets.ends.push(makeRecordNode(record, intersection, key));
        continue;
      case HGIntersectionType.Transit:
        node.buckets.transit.push(makeRecordNode(record, intersection, key));
        continue;
      case HGIntersectionType.Exact:
        placed.push({ record, intersection, exact: true });
        continue;
      default:
        // Contained | PartLeft | PartRight: belongs to a sub-interval when
        // there is one, otherwise it comes to rest here.
        if (childIt) descend.push(record);
        else placed.push({ record, intersection, exact: false });
    }
  }

  for (const { record, intersection, exact } of placed) {
    const recordNode = makeRecordNode(record, intersection, key);
    if (exact) node.buckets.exact.push(recordNode);
    else node.leaves.push(recordNode);
  }

  if (childIt && descend.length > 0) {
    for (const child of splitByChildInterval(
      ctx,
      descend,
      interval,
      endInterval,
      childIt,
    )) {
      node.children.push(
        buildLevel(
          ctx,
          child.records,
          child.interval,
          null,
          depth + 1,
          safeName(child.interval),
        ),
      );
    }
  }

  return node;
}

function splitByChildInterval(
  ctx: BuildContext,
  records: HGTimeRecordPlus[],
  interval: HGInterval,
  endInterval: HGInterval | null,
  childIt: HGIntervalType,
): { interval: HGInterval; records: HGTimeRecordPlus[] }[] {
  const [first, last] = IntervalsRange(interval, endInterval, childIt);
  const buckets = new Map<string, { n: bigint; records: HGTimeRecordPlus[] }>();

  const place = (n: bigint, record: HGTimeRecordPlus) => {
    if (n < first || n > last) return;
    const bucketKey = n.toString();
    const bucket = buckets.get(bucketKey);
    if (bucket) bucket.records.push(record);
    else buckets.set(bucketKey, { n, records: [record] });
  };

  for (const record of records) {
    const [from, to] = IntervalsRange(
      record.interval,
      record.end_interval,
      childIt,
    );
    const finished = !!record.end_interval;

    if (from === to) {
      place(from, record);
      continue;
    }

    if (ctx.options.spanning === "every") {
      const start = from > first ? from : first;
      const stop = to < last ? to : last;
      if (countN(start, stop) <= MAX_SPAN_EXPANSION) {
        for (let n = start; n <= stop; n = nextN(n)) place(n, record);
        continue;
      }
    }

    place(from, record);
    if (finished) place(to, record);
  }

  return [...buckets.values()]
    .sort((a, b) => (a.n === b.n ? 0 : a.n < b.n ? -1 : 1))
    .map((bucket) => ({
      interval: { n: bucket.n, it: childIt },
      records: bucket.records,
    }));
}

// --- arranging --------------------------------------------------------------

function sortLevel(node: LevelNode) {
  for (const bucket of BUCKET_KINDS) node.buckets[bucket].sort(compareRecordNodes);
  node.leaves.sort(compareRecordNodes);
  for (const child of node.children) {
    if (isLevelNode(child)) sortLevel(child);
  }
}

function reverseLevel(node: LevelNode) {
  for (const bucket of BUCKET_KINDS) node.buckets[bucket].reverse();
  node.leaves.reverse();
  node.children.reverse();
  for (const child of node.children) {
    if (isLevelNode(child)) reverseLevel(child);
  }
}

interface FinalizeContext {
  seen: Set<string>;
  spanning: SpanningMode;
}

function finalizeRecords(nodes: RecordNode[], ctx: FinalizeContext): RecordNode[] {
  const kept: RecordNode[] = [];
  for (const node of nodes) {
    const repeat = ctx.seen.has(node.record.id);
    node.repeat = repeat;
    ctx.seen.add(node.record.id);
    // "First only" means each record appears once, wherever it first lands in
    // render order — whether that second placement is its ending, or a partial
    // overlap that had no sub-level to descend into.
    if (repeat && ctx.spanning === "first") continue;
    kept.push(node);
  }
  return kept;
}

function sumRecords(nodes: RecordNode[]): number {
  return nodes.length;
}

/**
 * Walks the tree in render order to mark (or drop) records already shown
 * earlier, and totals every node so a collapsed header can state exactly how
 * many rows it hides. Levels left empty are removed so gaps can close over them.
 */
function finalizeLevel(node: LevelNode, ctx: FinalizeContext): number {
  node.buckets.exact = finalizeRecords(node.buckets.exact, ctx);
  node.buckets.starts = finalizeRecords(node.buckets.starts, ctx);

  node.children = node.children.filter((child) =>
    isLevelNode(child) ? finalizeLevel(child, ctx) > 0 : true,
  );
  node.leaves = finalizeRecords(node.leaves, ctx);

  node.buckets.ends = finalizeRecords(node.buckets.ends, ctx);
  node.buckets.transit = finalizeRecords(node.buckets.transit, ctx);

  node.total =
    sumRecords(node.buckets.exact) +
    sumRecords(node.buckets.starts) +
    sumRecords(node.leaves) +
    sumRecords(node.buckets.ends) +
    sumRecords(node.buckets.transit) +
    node.children.reduce(
      (sum, child) => sum + (isLevelNode(child) ? child.total : 0),
      0,
    );

  return node.total;
}

function insertGaps(node: LevelNode, levelTypes: HGIntervalType[], desc: boolean) {
  const childIt = levelTypes[node.depth];
  const levels = node.children.filter(isLevelNode);
  for (const child of levels) insertGaps(child, levelTypes, desc);
  if (!childIt || levels.length === 0) return;

  // Gaps are found in chronological order regardless of how they are displayed.
  const ascending = desc ? [...levels].reverse() : levels;
  const [first, last] = IntervalsRange(node.interval, node.endInterval, childIt);

  const merged: (LevelNode | GapNode)[] = [];
  const addGap = (from: bigint, to: bigint) => {
    merged.push({
      kind: "gap",
      key: `${node.key}/gap:${from}`,
      from: { n: from, it: childIt },
      to: { n: to, it: childIt },
      count: countN(from, to),
    });
  };

  let expected = first;
  for (const child of ascending) {
    if (child.interval.n > expected) addGap(expected, prevN(child.interval.n));
    merged.push(child);
    expected = nextN(child.interval.n);
  }
  if (expected <= last) addGap(expected, last);

  node.children = desc ? merged.reverse() : merged;
}

// --- entry point ------------------------------------------------------------

export function buildRecordTree(
  records: HGTimeRecordPlus[],
  interval: HGInterval,
  endInterval: HGInterval | null,
  rootName: string,
  options: RecordTreeOptions,
): RecordTree {
  const levelTypes = resolveLevelTypes(interval, endInterval, options);
  const ctx: BuildContext = { levelTypes, options };

  const root = buildLevel(ctx, records, interval, endInterval, 0, rootName);

  sortLevel(root);
  if (options.order === "desc") reverseLevel(root);
  finalizeLevel(root, { seen: new Set(), spanning: options.spanning });
  if (options.showGaps) insertGaps(root, levelTypes, options.order === "desc");

  return { root, levelTypes, total: root.total };
}
